import { create } from 'zustand';

/**
 * Patient-chart actions published for the global MaieLauncherPill speed-dial.
 *
 * The launcher pill lives in MainLayout (outside the patient page), but its
 * "Write note" / "Add file" speed-dial items operate on the chart you're
 * currently viewing — they open that page's own add-note modal and attach
 * picker. Rather than thread those handlers through layout context, the
 * PatientDetailsPage registers them here on mount and clears them on unmount;
 * the pill reads `actions` and only shows the patient items when a chart is
 * mounted. Off a patient page, `actions` is null and the dial falls back to
 * its global items (Record / Scan).
 */
export interface ChartActions {
  patientId: string;
  patientName?: string;
  /** Open the chart's add-note composer. */
  onWriteNote: () => void;
  /** Trigger the chart's attach/photo picker. */
  onAddFile: () => void;
}

interface ChartActionsState {
  actions: ChartActions | null;
  setActions: (a: ChartActions) => void;
  /**
   * Clear the registered actions. Pass the owning patientId so a mount/unmount
   * race (new chart registers before the old one's cleanup runs) doesn't wipe
   * the incoming registration — only the owner may clear its own actions.
   */
  clearActions: (patientId?: string) => void;
}

export const useChartActionsStore = create<ChartActionsState>((set) => ({
  actions: null,
  setActions: (a) => set({ actions: a }),
  clearActions: (patientId) =>
    set((s) =>
      !patientId || s.actions?.patientId === patientId ? { actions: null } : s,
    ),
}));
